import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Input,
  CardHeader,
  CardFooter,
  Button,
  Form,
  Collapse,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
  getUser as onGetUser,
  updateUser as onUpdateUser,
  changeUserPassword as onChangeUserPassword,
} from "../../../slices/thunks";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { useParams } from "react-router-dom";

const selectUserState = createSelector(
  (state) => state.User,
  (user) => ({
    user: user.user || null,
    isUserSuccess: user.isUserSuccess,
    error: user.error,
  })
);

const groupOptions = {
  Admin: "3",
  Editor: "2",
  Viewer: "1",
};

const UserDetails = () => {
  const dispatch = useDispatch();
  const { user, isUserSuccess, error } = useSelector(selectUserState);
  const { userProfile, loading: profileLoading } = useProfile();
  const { id } = useParams();
  const isViewer = useMemo(() => {
    return userProfile?.user?.groups?.includes("Viewer") || false;
  }, [userProfile]);
  const [formData, setFormData] = useState(user);
  const [formDataPass, setFormDataPass] = useState({
    password: "",
    password_verify: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    dispatch(onGetUser({ user_id: id }));
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name == "group_id") {
      setFormData((prevData) => ({
        ...prevData,
        groups: Object.keys(groupOptions).find(
          (key) => groupOptions[key] === value
        ),
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormDataPass((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(onUpdateUser(formData)).unwrap();

      setIsEditing(false);
    } catch (error) {
      console.error("Save failed:", error);
      setFormData(user);
    }
  };

  const handleSavePass = async () => {
    if (!formDataPass.password || !formDataPass.password_verify) {
      console.error("Both password fields are required.");
      return;
    }

    if (formDataPass.password !== formDataPass.password_verify) {
      console.error("Passwords do not match.");
      return;
    }

    try {
      await dispatch(
        onChangeUserPassword({
          user_id: formData?.id,
          password: formDataPass.password,
          password_verify: formDataPass.password_verify,
        })
      ).unwrap();

      setFormDataPass({ password: "", password_verify: "" });
    } catch (error) {
      console.error("Save failed:", error);
      console.error("Failed to update password. Please try again.");
    }
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  if (user == null) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="username">Username</Label>
                      <Input
                        type="text"
                        id="username"
                        name="username"
                        value={formData?.username || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="first_name">First Name</Label>
                      <Input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData?.first_name || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="last_name">Last Name</Label>
                      <Input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData?.last_name || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="email">Email</Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData?.email || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="group">Group</Label>
                      <Input
                        type="select"
                        id="group_id"
                        name="group_id"
                        value={groupOptions[formData?.groups] || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      >
                        <option value="3">Admin</option>
                        <option value="2">Editor</option>
                        <option value="1">Viewer</option>
                      </Input>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3 form-check">
                      <Input
                        type="checkbox"
                        id="is_superuser"
                        name="is_superuser"
                        checked={!!formData?.is_superuser}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: e.target.name,
                              value: e.target.checked,
                            },
                          })
                        }
                        disabled={!isEditing}
                        className="form-check-input"
                      />
                      <Label for="is_superuser" className="form-check-label">
                        Super User
                      </Label>
                    </div>
                    <div className="mb-3 form-check">
                      <Input
                        type="checkbox"
                        id="is_staff"
                        name="is_staff"
                        checked={!!formData?.is_staff}
                        onChange={(e) =>
                          handleInputChange({
                            target: {
                              name: e.target.name,
                              value: e.target.checked,
                            },
                          })
                        }
                        disabled={!isEditing}
                        className="form-check-input"
                      />
                      <Label for="is_staff" className="form-check-label">
                        Staff
                      </Label>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter className="d-flex justify-content-between align-items-center">
                {!isViewer && (
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={() => {
                        if (isEditing) {
                          setFormData(user);
                        }
                        toggleEdit();
                      }}
                    >
                      {isEditing ? "Cancel Edit" : "Edit"}
                    </Button>
                    {isEditing && (
                      <Button color="success" onClick={handleSave}>
                        Save
                      </Button>
                    )}
                  </div>
                )}
              </CardFooter>
            </Card>
          </Col>
        </Row>
        <Form>
          <Row className="justify-content-center">
            <Col md={8}>
              <Card>
                <CardHeader>
                  <Label for="surname">Password Reset</Label>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label for="password">Password</Label>
                        <Input
                          type="password"
                          placeholder="******"
                          id="password"
                          name="password"
                          value={formDataPass.password}
                          onChange={handlePasswordChange}
                          autoComplete="new-password"
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label for="password_verify">Verify Password</Label>
                        <Input
                          type="password"
                          placeholder="******"
                          id="password_verify"
                          name="password_verify"
                          value={formDataPass.password_verify}
                          onChange={handlePasswordChange}
                          autoComplete="new-password"
                        />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter className="d-flex justify-content-between align-items-center">
                  {!isViewer && (
                    <div className="d-flex gap-2">
                      <Button color="danger" onClick={handleSavePass}>
                        Reset Password
                      </Button>
                    </div>
                  )}
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default UserDetails;
